import { DeleteOutline } from '@mui/icons-material';
import AnalyticsOutlined from '@mui/icons-material/AnalyticsOutlined';
import CodeOutlined from '@mui/icons-material/CodeOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ShareOutlined from '@mui/icons-material/ShareOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import { Stack, Switch, TableCell, TableRow, Tooltip } from '@mui/material';
import MUIModal from '@mui/material/Modal';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CHAT_WIDGET_PUBLIC_KEY } from '../../../config/appConfig';
import { useAgentRoles } from '../../../contexes/AgentRolesProvider';
import AuthContext from '../../../contexes/AuthContext';
import DashboardContext from '../../../contexes/DashboardContext';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import useAgentStatusToggle from '../../../hooks/useAgentStatusToggle';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  AgentStatus as AgentStatusInterface,
  AgentType,
} from '../../../interfaces/Agent';
import { ResponseObject } from '../../../interfaces/App';
import {
  ChatAgent,
  ChatAgentWithAttachedKnowledge,
} from '../../../interfaces/ChatAgent';
import { ResourceAccess } from '../../../interfaces/ResourceAccess';
import {
  deleteChatAgent,
  updateChatAgent,
} from '../../../services/chatAgentService';
import {
  canEditAgentResource,
  canManageAgentAccess,
  toAccessPayload,
} from '../../../utils/resourceAccess';
import TransferAgentDialog from '../../admin/TransferAgentDialog';
import Button from '../../shared/Button/Button';
import CodeSnippet from '../../shared/CodeSnippet/CodeSnippet';
import Modal from '../../shared/Modal/Modal';
import StyledModal from '../../shared/Modal/StyledModal';
import ResourceVisibilityBadge from '../../shared/ResourceVisibilityBadge/ResourceVisibilityBadge';
import ShareResourceDialog from '../../shared/ShareResourceDialog/ShareResourceDialog';
import AgentAvatar from '../../shared/Table/AgentAvatar';
import AgentStatus from '../../shared/Table/AgentStatus';
import TextTruncated from '../../shared/TextTruncated/TextTruncated';

interface TableItemProps {
  item: ChatAgentWithAttachedKnowledge;
  items: ChatAgent[];
}

const TableItem = ({ item: chatAgent, items }: TableItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayMessage = useSnackBar();
  const { setAgentsCount } = useContext(DashboardContext);
  const { currentUser } = useContext(AuthContext);
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const { getRoleById, loading } = useAgentRoles();

  // Use the custom hook for status toggling
  const {
    agentStatus,
    isUpdating,
    onStatusClick,
    changeStatusModalOpen,
    setChangeStatusModalOpen,
  } = useAgentStatusToggle({
    agentId: chatAgent.id,
    initialStatus: chatAgent.status || '',
    updateService: updateChatAgent,
    requireSubscription: true,
    analyticsSource: 'chat_agent_table_page',
  });

  const [codeSnippetModalOpen, setCodeSnippetModalOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const canShare = canManageAgentAccess(currentUser, chatAgent);
  const canEdit = canEditAgentResource(currentUser, chatAgent);
  const actionIconButtonStyle = {
    minWidth: '44px',
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    background: '#F1F5F9',
    borderColor: 'rgba(15, 23, 42, 0.06)',
    color: '#1841ff',
    padding: '0.5rem',
  };

  const navigateToEditChatAgent = (agentId: string | undefined) => {
    if (agentId) {
      navigate(`/agents/chat/edit/${agentId}`);
    }
  };

  const handleDeleteChatAgent = async (agentId: string) => {
    track(AnalyticsEvents.AGENT_DELETION_ATTEMPTED, { agentId });

    try {
      const result: ResponseObject = await deleteChatAgent(agentId);

      if (result.status === 'success') {
        const remainingAgents = items.filter(item => item.id !== agentId);
        setAgentsCount(remainingAgents.length);

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['chat-agents'] }),
          queryClient.invalidateQueries({ queryKey: ['knowledges'] }),
          queryClient.invalidateQueries({
            queryKey: ['knowledges-with-attachment'],
          }),
        ]);

        displayMessage(result.message, 'success');
        track(AnalyticsEvents.AGENT_DELETION_SUCCESS, { agentId });
      } else {
        track(AnalyticsEvents.AGENT_DELETION_FAILURE, {
          agentId,
          reason: result.message,
        });
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        displayMessage(
          t('Oops! Something went wrong, please reload the page.'),
          'error'
        );
        track(AnalyticsEvents.AGENT_DELETION_FAILURE, {
          agentId,
          reason: err.message,
        });
      }
    }
  };

  const handleSaveAccess = async (access: ResourceAccess) => {
    try {
      await updateChatAgent(chatAgent.id, { access: toAccessPayload(access) });
      await queryClient.invalidateQueries({ queryKey: ['chat-agents'] });
      displayMessage(t('Access updated.'), 'success');
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
      }
      displayMessage(t('Failed to update access.'), 'error');
    }
  };

  return (
    <TableRow
      key={chatAgent.id}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row">
        <AgentAvatar
          agentName={chatAgent?.name ?? ''}
          agentId={chatAgent.id}
          imageFileName={
            chatAgent.custom_initialization?.header_avatar_url ?? ''
          }
          agentType={AgentType.CHAT}
        />
      </TableCell>
      {/* NOTE: Will enable this later on. */}
      {/*
      <TableCell align="center">
        <TextTruncated title={chatAgent.init_system_prompt} width="200px">
          {loading ? (
            <Skeleton variant="text" />
          ) : (
            getRoleById(chatAgent?.role_id ?? '')?.name
          )}
        </TextTruncated>
      </TableCell>
      */}
      <TableCell align="center">
        <ResourceVisibilityBadge access={chatAgent.access} />
      </TableCell>
      <TableCell align="center">
        <TextTruncated title={chatAgent.attachedKnowledge?.name} width="100px">
          {chatAgent.attachedKnowledge?.name}
        </TextTruncated>
      </TableCell>
      <TableCell align="center">
        <Stack direction="row" spacing={1}>
          <Switch
            name="status"
            onClick={onStatusClick}
            disabled={!canEdit || isUpdating}
            checked={
              agentStatus === AgentStatusInterface.ONLINE ||
              agentStatus === AgentStatusInterface.IN_REVIEW
            }
            sx={{
              width: 42,
              height: 22,
              p: 0,
              '& .MuiSwitch-switchBase': {
                p: '2px',
                transitionDuration: '180ms',
                '&.Mui-checked': {
                  transform: 'translateX(20px)',
                  color: '#FFFFFF',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#2147F6',
                    opacity: 1,
                  },
                },
              },
              '& .MuiSwitch-thumb': {
                width: 18,
                height: 18,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.25)',
              },
              '& .MuiSwitch-track': {
                borderRadius: 11,
                backgroundColor: '#D1D5DB',
                opacity: 1,
              },
            }}
          />
          <AgentStatus status={agentStatus as AgentStatusInterface} />
          <Modal
            modalShouldBeOpen={changeStatusModalOpen}
            setChangeStatusModalOpen={setChangeStatusModalOpen}
            showButton={false}
            item={chatAgent}
            callback={() => navigate('/pricing')}
            mainPromptText={t(
              'In order to change the agent status to online you need to have an active subscription'
            )}
            description={t('Subscribe to a plan and try again')}
            confirmText={t('Choose plan')}
            denyText={t('Cancel')}
          />
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {currentUser?.permissions?.super_admin && (
            <>
              <Tooltip
                title={t('Transfer to client')}
                placement="right-start"
                arrow
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    padding="0.5rem"
                    style={actionIconButtonStyle}
                    onClick={() => setTransferDialogOpen(true)}
                  >
                    <SwapHorizOutlined />
                  </Button>
                </span>
              </Tooltip>
              {transferDialogOpen && (
                <TransferAgentDialog
                  agentId={chatAgent.id as string}
                  agentName={chatAgent.name ?? ''}
                  agentType="chat"
                  onClose={() => setTransferDialogOpen(false)}
                  onSuccess={() => {
                    queryClient.invalidateQueries({
                      queryKey: ['chat-agents'],
                    });
                  }}
                />
              )}
            </>
          )}
          {canShare && (
            <>
              <Tooltip title={t('Share')} placement="right-start" arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    padding="0.5rem"
                    style={actionIconButtonStyle}
                    onClick={() => setShareDialogOpen(true)}
                  >
                    <ShareOutlined />
                  </Button>
                </span>
              </Tooltip>
              <ShareResourceDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                resourceName={chatAgent.name ?? ''}
                resourceLabelKey="chat agent"
                ownerUserId={chatAgent.user_id ?? currentUser?.id}
                initialAccess={chatAgent.access}
                onSave={handleSaveAccess}
              />
            </>
          )}
          <Tooltip title={t('View Code Snippet')} placement="right-start" arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                padding="0.5rem"
                style={actionIconButtonStyle}
                onClick={() => setCodeSnippetModalOpen(true)}
              >
                <CodeOutlined />
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={t('View analytics dashboard')}
            placement="right-start"
            arrow
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                padding="0.5rem"
                style={actionIconButtonStyle}
                onClick={() => navigate(`/agents/chat/${chatAgent.id}`)}
              >
                <AnalyticsOutlined />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('Edit chat agent')} placement="right-start" arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                padding="0.5rem"
                style={actionIconButtonStyle}
                onClick={() => navigateToEditChatAgent(chatAgent.id)}
                disabled={!canEdit}
              >
                <EditOutlined />
              </Button>
            </span>
          </Tooltip>
          {canEdit && (
            <Modal
              item={chatAgent}
              callback={handleDeleteChatAgent}
              mainPromptText={t(
                'Do you really want to delete agent {{name}}?',
                {
                  name: chatAgent.name,
                }
              )}
              confirmText={t('Yes, Delete')}
              denyText={t('Cancel')}
              openModalButtonText=""
              toolTipText={t('Delete')}
              openModalIcon={<DeleteOutline />}
            />
          )}
        </Stack>
        <MUIModal
          open={codeSnippetModalOpen}
          onClose={() => setCodeSnippetModalOpen(false)}
        >
          <StyledModal sx={{ width: { sm: '600px', md: '800px' } }}>
            <CodeSnippet
              appId={chatAgent.id}
              publicKey={CHAT_WIDGET_PUBLIC_KEY}
            />
          </StyledModal>
        </MUIModal>
      </TableCell>
    </TableRow>
  );
};

export default TableItem;
